import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { statsApi } from '@/lib/api/stats';
import { DEFAULT_RANGE_DAYS, lastNDays } from '@/lib/dateRange';
import { formatCount, formatPercent } from '@/lib/formatters';
import { DateRangePicker } from '@/components/charts/DateRangePicker';
import { TimelineChart } from '@/components/charts/TimelineChart';
import { BreakdownList } from '@/components/charts/BreakdownList';
import { HeatmapChart } from '@/components/charts/HeatmapChart';
import { labelFor } from '@/lib/breakdownIcons';
import type {
  AppRecord,
  BreakdownDim,
  Campaign,
  DateRange,
  EventType,
  Me,
} from '@/lib/types';

async function fetchMe(): Promise<Me> {
  const { data } = await api.get<Me>('/v1/me');
  return data;
}
async function fetchApps(): Promise<AppRecord[]> {
  const { data } = await api.get<AppRecord[]>('/v1/apps');
  return data;
}
async function fetchCampaigns(): Promise<Campaign[]> {
  const { data } = await api.get<Campaign[]>('/v1/campaigns');
  return data;
}

interface BreakdownCardProps {
  dim: BreakdownDim;
  type: EventType;
  range: DateRange;
  title: string;
  description: string;
}

function TenantBreakdownCard({
  dim,
  type,
  range,
  title,
  description,
}: BreakdownCardProps) {
  const q = useQuery({
    queryKey: queryKeys.tenantBreakdown(dim, type, range),
    queryFn: () => statsApi.tenantBreakdown(dim, type, range),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
            <Skeleton className="h-6 w-2/3" />
          </div>
        )}
        {q.isError && <p className="text-sm text-destructive">Could not load.</p>}
        {q.data && <BreakdownList data={q.data} dim={dim} />}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState<DateRange>(() => lastNDays(DEFAULT_RANGE_DAYS));

  const me = useQuery({ queryKey: queryKeys.me, queryFn: fetchMe });
  const apps = useQuery({ queryKey: queryKeys.apps, queryFn: fetchApps });
  const campaigns = useQuery({ queryKey: queryKeys.campaigns(), queryFn: fetchCampaigns });

  const empty = (apps.data?.length ?? 0) === 0 && (campaigns.data?.length ?? 0) === 0;
  const noApps = apps.isSuccess && (apps.data?.length ?? 0) === 0;

  const overview = useQuery({
    queryKey: queryKeys.statsOverview(range),
    queryFn: () => statsApi.overview(range),
    enabled: !empty,
  });
  const timeline = useQuery({
    queryKey: queryKeys.tenantTimeline(range),
    queryFn: () => statsApi.tenantTimeline(range),
    enabled: !empty,
  });
  const heatmap = useQuery({
    queryKey: queryKeys.tenantHeatmap('click', range),
    queryFn: () => statsApi.tenantHeatmap('click', range),
    enabled: !empty,
  });

  // First-run customers get the dedicated wizard instead of the dashboard's
  // empty state. The bar is "no apps yet" — once you register your first app
  // we keep you on the dashboard even if you haven't created a campaign,
  // because campaign-less is a valid mid-onboarding state to show empty KPIs.
  if (noApps) {
    return <Navigate to="/welcome" replace />;
  }

  if (empty) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Welcome to Pulse</h1>
          {me.data ? (
            <p className="text-sm text-muted-foreground">
              Signed in as <span className="font-medium">{me.data.email}</span>
            </p>
          ) : (
            <Skeleton className="h-4 w-48" />
          )}
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Get started</CardTitle>
            <CardDescription>
              Three steps to your first attributed install.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal space-y-2 pl-5">
              <li>
                <Link
                  to="/apps"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Register your first app
                </Link>{' '}
                — name, package / bundle, and store URLs.
              </li>
              <li>
                <Link
                  to="/settings/keys"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Grab your publishable key
                </Link>{' '}
                and drop it into the mobile SDK init.
              </li>
              <li>
                <Link
                  to="/campaigns"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Create a campaign
                </Link>{' '}
                — paste the tracking URL into your ad / email / QR.
              </li>
            </ol>
            <div className="flex gap-2 pt-2">
              <Button asChild>
                <Link to="/apps">Register an app</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/campaigns">Create a campaign</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totals = overview.data?.totals;
  const top = overview.data?.topCampaigns ?? [];
  // Tenant-wide "engagement" combines both first-touch event types:
  //   - click          (direct redirect to store, REDIRECT campaigns)
  //   - landing_click  (button tap on the landing page, LANDING campaigns
  //                     and REDIRECT fallback)
  // Showing only `clicks` here hid all LANDING-campaign activity from the
  // KPI row even when the chart below clearly showed it.
  const firstTouch = (totals?.clicks ?? 0) + (totals?.landingClicks ?? 0);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Overview</h1>
          <p className="text-sm text-muted-foreground">
            All campaigns for{' '}
            <span className="font-medium">{me.data?.email ?? '—'}</span>.
          </p>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </header>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardDescription>Clicks</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {formatCount(firstTouch)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Installs</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {formatCount(totals?.installs ?? 0)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Signups</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {formatCount(totals?.signups ?? 0)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Install → Click rate</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {formatPercent(totals?.installs, firstTouch)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {totals && totals.bots > 0 && (
        <p className="text-xs text-muted-foreground">
          Filtered <span className="font-medium">{formatCount(totals.bots)}</span>{' '}
          bot requests from charts below.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Activity over time</CardTitle>
          <CardDescription>
            Daily clicks, landing clicks, installs and signups across all campaigns.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timeline.isLoading && <Skeleton className="h-[280px] w-full" />}
          {timeline.isError && (
            <p className="text-sm text-destructive">Could not load timeline.</p>
          )}
          {timeline.data && <TimelineChart data={timeline.data} />}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TenantBreakdownCard
          dim="source"
          type="click"
          range={range}
          title="Traffic by source"
          description="Clicks split across campaign sources."
        />
        <TenantBreakdownCard
          dim="country"
          type="click"
          range={range}
          title="Country"
          description="Clicks by ISO country code."
        />
        <TenantBreakdownCard
          dim="target_platform"
          type="click"
          range={range}
          title="Store destination"
          description="Where clicks pointed — iOS, Android or web."
        />
        <TenantBreakdownCard
          dim="device_class"
          type="click"
          range={range}
          title="Device class"
          description="Desktop, phone or tablet."
        />
        <TenantBreakdownCard
          dim="os"
          type="click"
          range={range}
          title="OS"
          description="Visitor operating systems."
        />
        <TenantBreakdownCard
          dim="webview_app"
          type="click"
          range={range}
          title="In-app browsers"
          description="Traffic from inside Instagram / TikTok / etc."
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top campaigns</CardTitle>
          <CardDescription>Ranked by clicks in the selected range.</CardDescription>
        </CardHeader>
        <CardContent>
          {overview.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {top.length === 0 && overview.data && (
            <p className="text-sm text-muted-foreground">
              No campaign activity in this range.
            </p>
          )}
          {top.length > 0 && (
            <ul className="divide-y divide-border">
              {top.map((c) => (
                <li
                  key={c.code}
                  className="flex items-center justify-between gap-3 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{c.name ?? c.code}</p>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-mono">{c.code}</span>
                      {c.source && (
                        <>
                          {' · '}
                          <Badge variant="secondary" className="ml-1">
                            {labelFor('source', c.source)}
                          </Badge>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-6 tabular-nums">
                    <span>
                      <span className="text-muted-foreground">Clicks </span>
                      {formatCount(c.clicks)}
                    </span>
                    <span>
                      <span className="text-muted-foreground">Installs </span>
                      {formatCount(c.installs)}
                    </span>
                    <span>
                      <span className="text-muted-foreground">Signups </span>
                      {formatCount(c.signups)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>When clicks come in</CardTitle>
          <CardDescription>
            Day-of-week × hour-of-day heatmap (UTC).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {heatmap.isLoading && <Skeleton className="h-[240px] w-full" />}
          {heatmap.isError && (
            <p className="text-sm text-destructive">Could not load heatmap.</p>
          )}
          {heatmap.data && <HeatmapChart data={heatmap.data} />}
        </CardContent>
      </Card>
    </div>
  );
}
